import type { HostMessagePayload } from './host-message-payloads.types';

/**
 * Enumeration of all supported host message types.
 * Each type corresponds to a specific action in the embedded communication protocol.
 */
export enum HostMessageType {
  /** @host-message-communication Portal → Host */
  AwaitingToken = 'awaiting-token',
  /** @host-message-communication Portal → Host */
  Authenticated = 'authenticated',
  /** @host-message-communication Portal → Host */
  AuthenticationFailed = 'authentication-failed',

  /** @host-message-communication Host → Portal */
  TokenSetup = 'token-setup',

  /** @host-message-communication Portal → Host */
  AddNewModel = 'add-new-model',
  /** @host-message-communication Portal → Host */
  ModelUploaded = 'model-uploaded',
  /** @host-message-communication Portal → Host */
  ModelDeleted = 'model-deleted',

  /** @host-message-communication Portal → Host */
  ViewerReady = 'viewer-ready',
  /** @host-message-communication Portal → Host */
  ViewerError = 'viewer-error',

  /** @host-message-communication Host → Portal */
  ExternalCommand = 'external-command',
  /** @host-message-communication Portal → Host */
  CommandResult = 'command-result',

  /** @host-message-communication Portal → Host */
  AwaitingUploadSetup = 'awaiting-upload-setup',

  /** @host-message-communication Host → Portal */
  UploadSetup = 'upload-setup',
  /** @host-message-communication Host → Portal */
  ModelUpload = 'model-upload',

  /** @host-message-communication Portal → Host */
  ModelUploadStatus = 'model-upload-status',
  /** @host-message-communication Portal → Host */
  CancelModelUpload = 'cancel-model-upload',
  /** @host-message-communication Portal → Host */
  CancelModelSetup = 'cancel-model-setup',

  /** @host-message-communication Portal → Host */
  Initialized = 'initialized',
  /** @host-message-communication Portal → Host */
  OpenedPage = 'opened-page',
  /** @host-message-communication Portal → Host */
  Closed = 'closed',
  /** @host-message-communication Portal → Host */
  AwaitingScene = 'awaiting-scene',
  /** @host-message-communication Host → Portal */
  SceneSetup = 'scene-setup',
  /** @host-message-communication Portal → Host */
  TaskSelected = 'task-selected',
  /** @host-message-communication Portal → Host */
  TasksChecked = 'tasks-checked',
  /** @host-message-communication Portal → Host */
  CreateTask = 'create-task',
  /** @host-message-communication Portal → Host */
  RequestLogout = 'request-logout',
}

/** Typed host message structure */
export interface HostMessage<T = HostMessagePayload> {
  type: HostMessageType;
  payload?: T;
  timestamp?: number;
  correlationId?: string;
}

/** Configuration for timeout/retry behavior */
export interface TimeoutConfig {
  /** Timeout in milliseconds before retry or failure */
  timeoutMs: number;
  /** Maximum number of retry attempts (0 = no retries) */
  maxRetries: number;
  /** Interval between retries in milliseconds */
  retryIntervalMs: number;
}

/** Default timeout configuration */
export const DEFAULT_TIMEOUT_CONFIG: TimeoutConfig = {
  timeoutMs: 30000,
  maxRetries: 0,
  retryIntervalMs: 10000,
};

/** Configuration for awaiting token with periodic resend */
export const AWAITING_TOKEN_CONFIG: TimeoutConfig = {
  timeoutMs: 120000, // 2 minutes total
  maxRetries: 11, // Resend every 10 seconds for ~2 minutes
  retryIntervalMs: 10000,
};

/** Maximum timeout value for JavaScript timers (2^31 - 1 ms ≈ 24.8 days) */
const MAX_TIMEOUT_MS = 2147483647;
const MAX_RETRIES = 2147483647;
/** Configuration for persistent/never-ending retry (until completed or cancelled) */
export const PERSISTENT_TIMEOUT_CONFIG: TimeoutConfig = {
  timeoutMs: MAX_TIMEOUT_MS, // ~24.8 days (max for JS timers)
  maxRetries: MAX_RETRIES, // Unlimited retries
  retryIntervalMs: 10000, // Retry every 10 seconds
};
